/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Radio, Typography2 } from '@coursera/cds-core';

type Props<T> = {
  columns: Array<{
    accessor: string;
    label: string;
    width?: string;
    renderCell?: (item: T) => JSX.Element;
  }>;
  rows: Array<T & { id: string; name: string }>;
  maxHeight?: string;
  value?: string;
  emptyState?: React.ReactElement;
  onChange: (id: string) => void;
  'aria-label'?: string;
};

export const RadioTable = <T,>(props: Props<T>): React.ReactElement => {
  const { columns, rows, maxHeight, value, emptyState, onChange, ...otherProps } = props;
  return (
    <div
      css={css`
        border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
        background: var(--cds-color-neutral-background-primary-weak);
        border-radius: 8px;
        max-width: 858px;
        overflow: auto;
        max-height: ${maxHeight};

        label {
          margin: 0 !important;
          padding: 0 !important;
        }
      `}
      {...otherProps}
    >
      <table
        css={css`
          width: 100%;
        `}
      >
        <thead>
          <tr
            css={css`
              border-bottom: 2px solid var(--cds-color-neutral-stroke-primary-weak);
            `}
          >
            {columns.map((column) => (
              <th
                key={column.accessor}
                css={css`
                  background: var(--cds-color-neutral-background-primary-weak);
                  padding: var(--cds-spacing-200) var(--cds-spacing-300);
                  min-width: ${column.width};
                `}
              >
                <Typography2
                  component="h3"
                  variant="bodyPrimary"
                  css={css`
                    color: var(--cds-color-neutral-primary-weak);
                    font-weight: bold;
                    white-space: nowrap;
                  `}
                >
                  {column.label}
                </Typography2>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={columns.length}>{emptyState}</td>
            </tr>
          )}
          {rows.map((row) => (
            <tr
              onClick={() => onChange(row.id)}
              key={row.id}
              css={css`
                background: var(--cds-color-neutral-background-primary);
                border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);

                :hover {
                  background: var(--cds-color-neutral-background-primary-weak);
                }
              `}
            >
              {columns.map((column, index) => (
                <td
                  key={column.accessor}
                  css={css`
                    padding: var(--cds-spacing-300);
                    min-width: ${column.width};
                  `}
                >
                  {index === 0 ? (
                    <Radio
                      checked={row.id === value}
                      label={String(row.name)}
                      value={row.id}
                      onChange={() => onChange(row.id)}
                    />
                  ) : (
                    column.renderCell?.(row)
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RadioTable;
